import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Cpu, BrainCircuit, Clock, Zap, Thermometer, Activity, ChevronDown } from 'lucide-react';
import { GlassCard } from './SharedUI';
import { systemService, LiveStats } from '../services/systemService';
import { useSystemStats } from '../hooks/useSystemStats';
import { useApp } from '../contexts/AppContext';
import { useT } from '../lib/useT';

const SPARKLINE_POINTS = 60;
const SYSTEM_POLL_MS = 2000;
const TOKEN_POLL_MS = 10000;
const LATENCY_POLL_MS = 5000;
const SUBSCRIPTION_POLL_MS = 60000;

interface TokenData {
  grandTotal: number;
  recordCount: number;
  byProvider: Record<string, { totalTokens: number; calls: number }>;
}

interface LatencyStats {
  llm: { avgMs: number; lastMs: number; count: number };
  tts: { avgMs: number; lastMs: number; count: number };
  stt: { avgMs: number; lastMs: number; count: number };
}

interface SubStatus {
  tokensUsedThisMonth: number;
  monthlyTokenCap: number;
}

// ── helpers ──

function fmtTokens(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtRate(bytesPerSec: number) {
  if (!isFinite(bytesPerSec) || bytesPerSec <= 0) return '0 B/s';
  const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  let v = bytesPerSec;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function latencyColor(ms: number) {
  if (ms <= 0) return 'text-white/45';
  if (ms < 500) return 'text-green-400';
  if (ms < 2000) return 'text-amber-400';
  return 'text-red-400';
}

function healthStatus(score: number): { label: string; color: string } {
  if (score >= 90) return { label: 'Excellent', color: 'text-green-400' };
  if (score >= 70) return { label: 'Good', color: 'text-lime-400' };
  if (score >= 50) return { label: 'Fair', color: 'text-amber-400' };
  if (score >= 30) return { label: 'Poor', color: 'text-orange-400' };
  return { label: 'Critical', color: 'text-red-400' };
}

// ── SVG Sparkline ──

function Sparkline({ data, width, height, color, label, value }: {
  data: number[]; width: number; height: number; color: string; label: string; value: string;
}) {
  if (data.length < 2) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-[12px] font-bold text-white/55 w-8 shrink-0">{label}</span>
        <div className="flex-1 h-[36px] bg-white/[0.03] rounded-lg animate-pulse" />
        <span className="text-xs font-mono text-white/45 w-14 text-right">{value}</span>
      </div>
    );
  }

  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const h = height - 4;

  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => `${(i * stepX).toFixed(1)},${(h - ((v - min) / range) * h + 2).toFixed(1)}`).join(' ');
  const areaPts = `${pts} ${((data.length - 1) * stepX).toFixed(1)},${height} 0,${height}`;

  return (
    <div className="flex items-center gap-3">
      <span className="text-[12px] font-bold text-white/55 w-8 shrink-0">{label}</span>
      <svg width={width} height={height} className="shrink-0 overflow-visible">
        <polygon points={areaPts} fill={color} opacity={0.12} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-xs font-mono text-white/60 w-14 text-right">{value}</span>
    </div>
  );
}

// ── Collapse Header ──

export function CollapseHeader({
  icon,
  label,
  collapsed,
  onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="text-xs font-bold uppercase tracking-wider text-white/40 flex items-center gap-1.5 w-full group hover:text-white/60 transition-colors duration-200"
    >
      {icon} {label}
      <motion.span
        animate={{ rotate: collapsed ? -90 : 0 }}
        transition={{ duration: 0.2 }}
        className="ml-auto"
      >
        <ChevronDown size={12} />
      </motion.span>
    </button>
  );
}

export function CollapseSection({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <AnimatePresence initial={false}>
      {!collapsed && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden mt-1.5"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Main Component ──

export function NeuralSynthesisMonitor({ t, onOpenTokens }: { t?: any; onOpenTokens?: () => void }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const { aiConfig } = useApp();
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [subStatus, setSubStatus] = useState<SubStatus | null>(null);
  const [latency, setLatency] = useState<LatencyStats | null>(null);
  const prevTokenTotal = useRef<number>(0);
  const [tokenSpeed, setTokenSpeed] = useState<number>(0);
  // 系统资源数据统一由 useSystemStats 提供（socket 推送 + HTTP 兜底），三端与移动端同源
  const { stats: liveStats, netRate, cpuHistory, memHistory } = useSystemStats();

  // ── Collapse states ──
  const [sysCollapsed, setSysCollapsed] = useState(false);
  const [aiCollapsed, setAiCollapsed] = useState(false);
  const [perfCollapsed, setPerfCollapsed] = useState(false); // Latency + Sensors merged

  // ── IoT / Mijia 已迁移至独立 MijiaPanel 组件 ──

  // ── System stats 已由 useSystemStats 统一提供（见上方组件顶部）──

  // ── Token usage polling ──

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch('/api/llm/usage?days=1', { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        setTokenData(d);
        if (prevTokenTotal.current > 0 && d.grandTotal > prevTokenTotal.current) {
          setTokenSpeed((d.grandTotal - prevTokenTotal.current) / (TOKEN_POLL_MS / 1000));
        }
        prevTokenTotal.current = d.grandTotal;
      } catch {}
    };
    poll();
    const id = setInterval(poll, TOKEN_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Subscription polling ──

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch('/api/subscription/status', { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        setSubStatus({
          tokensUsedThisMonth: d.subscription?.tokensUsedThisMonth ?? 0,
          monthlyTokenCap: d.subscription?.monthlyTokenCap ?? 500000,
        });
      } catch {}
    };
    poll();
    const id = setInterval(poll, SUBSCRIPTION_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Latency polling ──

  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch('/api/monitor/latency');
        if (!r.ok) return;
        setLatency(await r.json());
      } catch {}
    };
    poll();
    const id = setInterval(poll, LATENCY_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Health score ──

  const healthScore = liveStats
    ? (() => {
        const cpu = liveStats.cpu_percent;
        const mem = liveStats.memory_percent;
        const maxTemp = liveStats.temperatures.length > 0
          ? Math.max(...liveStats.temperatures.map(t => t.celsius))
          : 0;
        let penalty: number;
        if (maxTemp > 0) {
          penalty = cpu * 0.4 + mem * 0.35 + Math.max(0, maxTemp - 50) * 1.0 * 0.25;
        } else {
          penalty = cpu * 0.5 + mem * 0.5;
        }
        return Math.max(0, Math.min(100, Math.round(100 - penalty)));
      })()
    : null;

  const health = healthScore != null ? healthStatus(healthScore) : null;

  // ── Format helpers ──

  const cpuFmt = liveStats ? `${Math.round(liveStats.cpu_percent)}%` : '--';
  const memFmt = liveStats
    ? `${Math.round(liveStats.memory_percent)}% (${liveStats.memory_used_gb.toFixed(1)}/${liveStats.memory_total_gb.toFixed(1)}G)`
    : '--';
  const diskColor = (pct: number) => (pct > 85 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#22d3ee');

  const modelLabel = aiConfig ? `${aiConfig.provider}/${aiConfig.model}` : '--';
  const tokenSpeedFmt = tokenSpeed > 0 ? `${Math.round(tokenSpeed)} tok/s` : '--';
  const todayTokens = tokenData ? fmtTokens(tokenData.grandTotal) : '--';
  const capPct = subStatus && subStatus.monthlyTokenCap > 0
    ? Math.round((subStatus.tokensUsedThisMonth / subStatus.monthlyTokenCap) * 100)
    : 0;

  return (
    <GlassCard className="p-5 rounded-[2.5rem] space-y-1.5 border-white/5 bg-black/30 backdrop-blur-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-black uppercase tracking-widest text-white/55 flex items-center gap-2">
          <Activity size={12} /> {t?.neuralSynthesis || '神经合成'}
        </h4>
        <div className="flex items-center gap-2">
          {health && (
            <span className={`text-[12px] font-bold ${health.color}`}>
              {healthScore}/{health.label}
            </span>
          )}
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_#22c55e]"
          />
        </div>
      </div>

      {/* Section 1: System Resources (collapsible) */}
      <div className="space-y-1">
        <CollapseHeader
          icon={<Cpu size={10} />}
          label="系统资源"
          collapsed={sysCollapsed}
          onToggle={() => setSysCollapsed(v => !v)}
        />
        <CollapseSection collapsed={sysCollapsed}>
          <div className="space-y-2">
            <Sparkline data={cpuHistory} width={200} height={28} color="#22d3ee" label="CPU" value={cpuFmt} />
            <Sparkline data={memHistory} width={200} height={28} color="#f59e0b" label="MEM" value={memFmt} />

            {/* 磁盘使用率（仅监控系统盘与小枫OS盘，排除 disk1） */}
            {liveStats?.disk?.length ? (
              <div className="space-y-1.5 pt-1">
                {liveStats.disk.map((d) => (
                  <div key={d.mount} className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-white/45 font-medium">
                        {d.mount === '/' ? (t?.sysDisk || '系统盘') : d.mount}
                      </span>
                      <span className="font-mono text-white/55">
                        {d.used_gb}/{d.total_gb}G · {d.percent}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(100, d.percent)}%`, background: diskColor(d.percent) }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* 负载 / 网络 / 交换 */}
            <div className="grid grid-cols-3 gap-2 pt-1">
              <div>
                <div className="text-[10px] text-white/40 font-bold uppercase mb-0.5">{t?.load || '负载'}</div>
                <div className="text-[11px] font-mono text-white/60">
                  {liveStats ? liveStats.loadavg.map((v) => v.toFixed(2)).join(' / ') : '--'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-white/40 font-bold uppercase mb-0.5">{t?.net || '网络'}</div>
                <div className="text-[11px] font-mono text-white/60 leading-tight">
                  <div>↓ {fmtRate(netRate.rx)}</div>
                  <div>↑ {fmtRate(netRate.tx)}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] text-white/40 font-bold uppercase mb-0.5">{t?.swap || '交换'}</div>
                <div className="text-[11px] font-mono text-white/60">
                  {liveStats && liveStats.swap.total_gb > 0
                    ? `${liveStats.swap.used_gb}/${liveStats.swap.total_gb}G`
                    : (t?.noSwap || '无')}
                </div>
              </div>
            </div>
          </div>
        </CollapseSection>
      </div>

      {/* Section 2: AI Inference (collapsible) */}
      <div className="space-y-1">
        <CollapseHeader
          icon={<BrainCircuit size={10} />}
          label="AI 推理"
          collapsed={aiCollapsed}
          onToggle={() => setAiCollapsed(v => !v)}
        />
        <CollapseSection collapsed={aiCollapsed}>
          <div className="grid grid-cols-2 gap-3 cursor-pointer" onClick={onOpenTokens}>
            <div>
              <div className="text-xs text-white/45 font-medium mb-0.5">{ui('模型','Model')}</div>
              <div className="text-xs font-mono text-white/60 truncate flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block shrink-0" />
                {modelLabel}
              </div>
            </div>
            <div>
              <div className="text-xs text-white/45 font-medium mb-0.5">{ui('速度','Speed')}</div>
              <div className="text-xs font-mono text-white/50">{tokenSpeedFmt}</div>
            </div>
            <div>
              <div className="text-xs text-white/45 font-medium mb-0.5">{ui('今日','Today')}</div>
              <div className="text-xs font-mono text-white/50">{todayTokens}</div>
            </div>
            <div>
              <div className="text-xs text-white/45 font-medium mb-0.5">{ui('月度上限','Month Cap')}</div>
              <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(capPct, 100)}%` }}
                    className={`h-full rounded-full ${capPct > 90 ? 'bg-red-500' : capPct > 70 ? 'bg-amber-500' : 'bg-cyan-400'}`}
                  />
                </div>
                <span className="text-[12px] font-mono text-white/55">{capPct}%</span>
              </div>
            </div>
          </div>
        </CollapseSection>
      </div>

      {/* Section 3: Latency + Sensors merged (collapsible) */}
      <div className="space-y-1">
        <CollapseHeader
          icon={<Clock size={10} />}
          label="性能监控"
          collapsed={perfCollapsed}
          onToggle={() => setPerfCollapsed(v => !v)}
        />
        <CollapseSection collapsed={perfCollapsed}>
          {/* Latency row */}
          <div className="flex gap-3 text-[12px] mb-2.5">
            {(['llm', 'tts', 'stt'] as const).map(k => {
              const d = latency?.[k];
              return (
                <div key={k} className="flex-1">
                  <div className="text-white/45 font-bold uppercase mb-0.5">{k.toUpperCase()}</div>
                  <div className={`font-mono ${latencyColor(d?.lastMs ?? 0)}`}>
                    {d && d.lastMs > 0 ? `${d.lastMs}ms` : '--'}
                  </div>
                  <div className="text-xs text-white/40">
                    avg {d && d.avgMs > 0 ? `${d.avgMs}ms` : '--'}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Sensors row — inline compact */}
          {liveStats && (
            <div className="flex items-center gap-2 flex-wrap">
              <Thermometer size={11} className="text-white/30 shrink-0" />
              {liveStats.temperatures.length > 0 ? (
                liveStats.temperatures.slice(0, 4).map((t, i) => (
                  <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white/5 text-white/50">
                    {t.label.slice(0, 10)} {Math.round(t.celsius)}°
                  </span>
                ))
              ) : (
                <span className="text-[11px] text-white/45">{t?.noSensorData || '无传感器数据'}</span>
              )}
              {liveStats.fan_speed_rpm != null && (
                <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white/5 text-white/50">
                  Fan {liveStats.fan_speed_rpm}rpm
                </span>
              )}
              {health && (
                <span className={`text-[11px] font-bold ml-auto ${health.color}`}>
                  {healthScore}/100
                </span>
              )}
            </div>
          )}
        </CollapseSection>
      </div>


    </GlassCard>
  );
}
